import { Container, FillGradient, Graphics } from 'pixi.js';
import { listen } from '../scene.js';
import { COLOR, DESIGN_HEIGHT, DESIGN_WIDTH } from './theme.js';

/** Размер кадра: макеты 1440 × 900 и портретный кадр телефона 720 × 1280 (SPEC.md, 16.3). */
export interface FrameSize {
  readonly width: number;
  readonly height: number;
}

export const LANDSCAPE: FrameSize = { width: DESIGN_WIDTH, height: DESIGN_HEIGHT };
export const PORTRAIT: FrameSize = { width: 720, height: 1280 };

/** Окно заметно выше, чем шире, — телефон в портрете. */
export function isPortrait(): boolean {
  return window.innerHeight > window.innerWidth * 1.15;
}

export function currentFrame(): FrameSize {
  return isPortrait() ? PORTRAIT : LANDSCAPE;
}

/**
 * Кадр экрана в координатах макета: вписывается в окно целиком.
 * Фон тянется на всё окно, содержимое обрезается по кадру. Размер кадра можно сменить
 * (поворот телефона) — экран перестраивает содержимое сам.
 */
export class DesignFrame extends Container {
  /** Содержимое экрана в координатах макета. */
  readonly content = new Container();
  private readonly unlisten: () => void;
  private readonly mask_ = new Graphics();
  private background: Graphics;
  size: FrameSize;

  constructor(background: Graphics, size: FrameSize = LANDSCAPE) {
    super();
    this.size = size;
    this.background = background;
    this.content.mask = this.mask_;
    this.addChild(background, this.mask_, this.content);
    this.drawMask();
    this.unlisten = listen('resize', () => this.fit());
    this.fit();
  }

  /** Новый размер кадра и фон; содержимое экран строит заново. */
  resize(size: FrameSize, background: Graphics): void {
    this.size = size;
    this.background.destroy();
    this.background = background;
    this.addChildAt(background, 0);
    this.drawMask();
    this.fit();
  }

  private drawMask(): void {
    this.mask_.clear().rect(0, 0, this.size.width, this.size.height).fill(COLOR.white);
  }

  /** Масштаб и положение кадра в окне; вызывается при изменении размера окна. */
  fit(): void {
    const { width, height } = this.size;
    const scale = Math.min(window.innerWidth / width, window.innerHeight / height);
    this.scale.set(scale);
    this.position.set(
      (window.innerWidth - width * scale) / 2,
      (window.innerHeight - height * scale) / 2,
    );
  }

  override destroy(): void {
    this.unlisten();
    super.destroy({ children: true });
  }
}

/** Запас фона за краями кадра, px макета. */
const BLEED = 4000;

/**
 * Фон экрана макета: радиальное свечение с центром в (`cx`, `cy`) — доли кадра —
 * и цветные пятна-ореолы.
 */
export function screenBackground(
  cx: number,
  cy: number,
  glows: readonly { x: number; y: number; r: number; color: number; alpha: number }[] = [],
  size: FrameSize = LANDSCAPE,
): Graphics {
  // Фон с запасом за пределы кадра: при любом соотношении сторон окна полей не видно.
  const x = -BLEED;
  const y = -BLEED;
  const w = size.width + 2 * BLEED;
  const h = size.height + 2 * BLEED;
  const g = new Graphics().rect(x, y, w, h).fill(COLOR.night);
  const radius = Math.max(size.width, size.height) * 0.7;
  g.rect(x, y, w, h).fill(
    new FillGradient({
      type: 'radial',
      center: { x: cx * size.width, y: cy * size.height },
      innerRadius: 0,
      outerCenter: { x: cx * size.width, y: cy * size.height },
      outerRadius: radius,
      colorStops: [
        { offset: 0, color: COLOR.sea },
        { offset: 0.52, color: COLOR.deep },
        { offset: 1, color: COLOR.night },
      ],
      textureSpace: 'global',
    }),
  );
  for (const glow of glows) {
    g.circle(glow.x, glow.y, glow.r).fill(
      new FillGradient({
        type: 'radial',
        center: { x: glow.x, y: glow.y },
        innerRadius: 0,
        outerCenter: { x: glow.x, y: glow.y },
        outerRadius: glow.r,
        colorStops: [
          {
            offset: 0,
            color: `rgba(${(glow.color >> 16) & 255}, ${(glow.color >> 8) & 255}, ${glow.color & 255}, ${glow.alpha})`,
          },
          {
            offset: 0.7,
            color: `rgba(${(glow.color >> 16) & 255}, ${(glow.color >> 8) & 255}, ${glow.color & 255}, 0)`,
          },
        ],
        textureSpace: 'global',
      }),
    );
  }
  return g;
}
